'use client';

import type { TagData } from 'src/types/tag-data';
import { useMemo } from 'react';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@savvy-kit/components/dropdown-menu';
import { Search, X } from 'lucide-react';

import { CategoryIndicator } from '../../tags/category-indicator';

export type TagsFilterContentProps = {
  readonly allTags: readonly TagData[];
  readonly selectedTagIds: readonly string[];
  readonly onToggleTag: (tagId: string) => void;
  readonly onClearAll: () => void;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly searchInputRef?: React.RefObject<HTMLInputElement | null>;
  readonly onEnterKeyDown?: () => void;
  readonly useDropdownContext?: boolean;
};

function TagsFilterContentBase({
  allTags,
  selectedTagIds,
  onToggleTag,
  onClearAll,
  search,
  onSearchChange,
  searchInputRef,
  onEnterKeyDown,
  useDropdownContext,
}: TagsFilterContentProps & { readonly useDropdownContext: boolean }) {
  const filteredTags = useMemo(() => {
    return allTags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()));
  }, [allTags, search]);

  const selectedTags = useMemo(() => {
    // Filter selected tags and order them by selection order
    const selected = filteredTags.filter((tag) => selectedTagIds.includes(tag.id));
    // Create a map for quick lookup of selection index
    const selectionOrderMap = new Map(selectedTagIds.map((id, index) => [id, index]));
    // Sort by selection order
    return selected.sort((a, b) => {
      const orderA = selectionOrderMap.get(a.id) ?? Infinity;
      const orderB = selectionOrderMap.get(b.id) ?? Infinity;
      return orderA - orderB;
    });
  }, [filteredTags, selectedTagIds]);

  const unselectedTags = useMemo(() => {
    // Filter unselected tags and sort by category id then alphabetically
    const unselected = filteredTags.filter((tag) => !selectedTagIds.includes(tag.id));
    return unselected.sort((a, b) => {
      // First sort by category id
      const categoryIdA = a.categoryId ?? '';
      const categoryIdB = b.categoryId ?? '';
      if (categoryIdA !== categoryIdB) {
        return categoryIdA.localeCompare(categoryIdB);
      }
      // If category ids are the same (or both null), sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }, [filteredTags, selectedTagIds]);

  const itemClassName = useDropdownContext
    ? 'px-2 py-1.5'
    : 'px-3 py-2 cursor-pointer hover:bg-component-hover flex items-center gap-2 min-w-0';
  const labelClassName = useDropdownContext
    ? 'px-2 py-1.5 text-xs font-medium text-secondary'
    : 'px-3 py-1.5 text-xs font-medium text-secondary';
  const separatorClassName = useDropdownContext ? undefined : 'bg-border-light h-px mt-1';

  return (
    <>
      <div className="p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-secondary" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
              // Maintain focus after state update to prevent dropdown from stealing focus
              if (searchInputRef) {
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 0);
              }
            }}
            onKeyDown={(e) => {
              // Prevent arrow keys from moving focus to dropdown items
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.stopPropagation();
              }
              // Close dropdown on Enter
              if (e.key === 'Enter' && onEnterKeyDown) {
                e.preventDefault();
                onEnterKeyDown();
              }
            }}
            className="pl-8 h-10 w-full min-w-0 rounded-md border border-light bg-transparent px-3 py-1 text-dark shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-border-light focus-visible:ring-2"
          />
        </div>
      </div>
      {useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />}
      <div className="max-h-[40vh] overflow-y-auto overflow-x-hidden">
        {selectedTags.length > 0 && (
          <>
            {useDropdownContext ? (
              <DropdownMenuLabel className={labelClassName}>Selected</DropdownMenuLabel>
            ) : (
              <div className={labelClassName}>Selected</div>
            )}
            {selectedTags.map((tag) => {
              const content = (
                <div className="flex items-center gap-2 min-w-0 cursor-pointer">
                  <Checkbox checked={true} className="shrink-0 pointer-events-none" tabIndex={-1} />
                  <CategoryIndicator hexColorCode={tag.hexColorCode} emoji={tag.emoji} smart={tag.isSmart === true} />

                  <span className="truncate min-w-0">{tag.name}</span>
                </div>
              );

              return useDropdownContext ? (
                <DropdownMenuItem
                  key={tag.id}
                  closeOnClick={false}
                  onClick={() => {
                    onToggleTag(tag.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </DropdownMenuItem>
              ) : (
                <div
                  key={tag.id}
                  onClick={() => {
                    onToggleTag(tag.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </div>
              );
            })}
            {unselectedTags.length > 0 &&
              (useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />)}
          </>
        )}
        {unselectedTags.length > 0 && (
          <>
            {unselectedTags.map((tag) => {
              const content = (
                <div className="flex items-center gap-2 min-w-0 cursor-pointer">
                  <Checkbox checked={false} className="shrink-0 pointer-events-none" tabIndex={-1} />
                  <CategoryIndicator hexColorCode={tag.hexColorCode} emoji={tag.emoji} smart={tag.isSmart === true} />

                  <span className="truncate min-w-0">{tag.name}</span>
                </div>
              );

              return useDropdownContext ? (
                <DropdownMenuItem
                  key={tag.id}
                  closeOnClick={false}
                  onClick={() => {
                    onToggleTag(tag.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </DropdownMenuItem>
              ) : (
                <div
                  key={tag.id}
                  onClick={() => {
                    onToggleTag(tag.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </div>
              );
            })}
          </>
        )}
        {selectedTags.length === 0 && unselectedTags.length === 0 && (
          <div className="px-2 py-4 text-center text-sm text-secondary">No options</div>
        )}
      </div>
      {selectedTagIds.length > 0 && (
        <>
          {useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />}
          {useDropdownContext ? (
            <DropdownMenuItem onClick={onClearAll} className="px-2 py-1.5 text-primary focus:text-primary">
              <X className="size-4 mr-2 text-primary" />
              Clear all
            </DropdownMenuItem>
          ) : (
            <div
              onClick={onClearAll}
              className="px-3 py-2 cursor-pointer hover:bg-component-hover text-primary flex items-center gap-2"
            >
              <X className="size-4 text-primary" />
              Clear all
            </div>
          )}
        </>
      )}
    </>
  );
}

export function TagsFilterContent(props: TagsFilterContentProps) {
  const { useDropdownContext = true, ...rest } = props;
  return <TagsFilterContentBase {...rest} useDropdownContext={useDropdownContext} />;
}
